import React, { useState, useEffect, useCallback } from 'react';
import { Exam, Question, ExamAttempt, StudentAnswer } from '../../types/exam';
import { examApi } from '../../services/exam.service';
import ExamQuestion from './ExamQuestion';
import ExamTimer from './ExamTimer';

interface ExamTakerProps {
  exam: Exam;
  onComplete: () => void;
  onBack: () => void;
}

export const ExamTaker: React.FC<ExamTakerProps> = ({ exam, onComplete, onBack }) => {
  const [attempt, setAttempt] = useState<ExamAttempt | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState<Map<string, StudentAnswer>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const shuffleArray = useCallback(<T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  const initializeExam = useCallback(async () => {
    try {
      setLoading(true);
      // Start exam attempt
      const newAttempt = await examApi.startExam(exam.examId);
      setAttempt(newAttempt);

      // Load questions
      const qs = await examApi.getExamQuestions(exam.examId);
      const shuffled = exam.shuffleQuestions ? shuffleArray(qs) : qs;
      setQuestions(shuffled);

      setError(null);
    } catch (err) {
      setError('Failed to start exam');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [exam.examId, exam.shuffleQuestions, shuffleArray]);

  useEffect(() => {
    initializeExam();
  }, [initializeExam]);

  const handleAnswerChange = async (questionId: string, answer: StudentAnswer) => {
    const newAnswers = new Map(answers);
    newAnswers.set(questionId, answer);
    setAnswers(newAnswers);

    // Auto-save answer
    if (attempt) {
      try {
        await examApi.saveAnswer(attempt.attemptId, {
          questionId,
          answer: answer.answer,
          timeSpent: 0,
        });
      } catch (err) {
        console.error('Failed to save answer:', err);
      }
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIdx < questions.length - 1) {
      setCurrentQuestionIdx(currentQuestionIdx + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIdx > 0) {
      setCurrentQuestionIdx(currentQuestionIdx - 1);
    }
  };

  const handleSubmitExam = async () => {
    if (!attempt) return;

    try {
      setLoading(true);
      const result = await examApi.submitExam(attempt.attemptId, {
        totalTimeSpent: Math.floor(
          (new Date().getTime() - new Date(attempt.startedAt).getTime()) / 1000
        ),
      });

      // Show result
      setAttempt(result);
      setShowConfirm(false);
      
      // Auto-redirect after 3 seconds
      setTimeout(onComplete, 3000);
    } catch (err) {
      setError('Failed to submit exam');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeExpired = () => {
    handleSubmitExam();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={onBack}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
        >
          Back to Exams
        </button>
      </div>
    );
  }

  if (!attempt || questions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Failed to load exam</p>
        <button
          onClick={onBack}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
        >
          Back
        </button>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIdx];
  const currentAnswer = answers.get(currentQuestion.questionId);

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{exam.title}</h1>
              <p className="text-gray-600">{exam.subject}</p>
            </div>
            <ExamTimer
              durationMinutes={exam.duration}
              onExpired={handleTimeExpired}
            />
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${((currentQuestionIdx + 1) / questions.length) * 100}%`,
              }}
            ></div>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Question {currentQuestionIdx + 1} of {questions.length}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Question Area */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-md p-6">
              <ExamQuestion
                question={currentQuestion}
                answer={currentAnswer}
                onChange={(answer) => handleAnswerChange(currentQuestion.questionId, answer)}
              />

              {/* Navigation */}
              <div className="flex gap-4 mt-8 pt-6 border-t">
                <button
                  onClick={handlePrevQuestion}
                  disabled={currentQuestionIdx === 0}
                  className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded disabled:cursor-not-allowed"
                >
                  ← Previous
                </button>

                <button
                  onClick={handleNextQuestion}
                  disabled={currentQuestionIdx === questions.length - 1}
                  className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded disabled:cursor-not-allowed"
                >
                  Next →
                </button>

                <div className="flex-1"></div>

                <button
                  onClick={() => setShowConfirm(true)}
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded font-medium"
                >
                  Submit Exam
                </button>
              </div>
            </div>
          </div>

          {/* Question Navigator */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4 sticky top-4">
              <h3 className="font-bold text-gray-800 mb-3">Questions</h3>
              <div className="grid grid-cols-4 gap-2 max-h-96 overflow-y-auto">
                {questions.map((q, idx) => (
                  <button
                    key={q.questionId}
                    onClick={() => setCurrentQuestionIdx(idx)}
                    className={`p-2 rounded text-sm font-medium transition ${
                      idx === currentQuestionIdx
                        ? 'bg-blue-500 text-white'
                        : answers.has(q.questionId)
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                    title={q.questionText.substring(0, 30)}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-600 mt-3">
                ✓ = Answered | Current = Highlighted
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Submission Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Submit Exam?</h2>
            <p className="text-gray-600 mb-2">
              You have answered <strong>{answers.size}</strong> out of{' '}
              <strong>{questions.length}</strong> questions.
            </p>
            <p className="text-gray-600 mb-6">
              This action cannot be undone. Are you sure you want to submit?
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded font-medium"
              >
                Continue Exam
              </button>
              <button
                onClick={handleSubmitExam}
                disabled={loading}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white px-4 py-2 rounded font-medium"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamTaker;
